import { useMemo } from 'react';
import { ModuleCategory, ModuleSubCategory } from '@/apolloClient.types';
import {
    useFetchAvailableModulesQuery,
    useFetchCompanyDetailsQuery,
} from '@/pages/CompanyManagementPage/shared/Companies.api';

export interface CompanyModuleAccessHook {
    loading: boolean;
    companyId: string | null;
    // Coarse checks
    hasAssessments: boolean;
    hasTraining: boolean;
    hasReporting: boolean;
    // Fine-grained assessment checks
    hasCognitiveAssessments: boolean;
    hasPsychologicalAssessments: boolean;
    hasPhysiologicalAssessments: boolean;
    // Reporting checks
    hasIndividualReport: boolean;
    hasTeamResilienceReport: boolean;
    // check performance module subscription
    hasWeightSubscription: boolean;
    hasIpptSubscription: boolean;
    hasSocSubscription: boolean;
    has20kmMarchSubscription: boolean;
    hasFinswimSubscription: boolean;
    hasBmpSubscription: boolean;
    hasPerformance: boolean;

    // check cognitive module subscription
    hasSartSubscription: boolean;
    hasRxnSubscription: boolean;
    hasSpatialPlanningSubscription: boolean;

    // check psychological module subscription
    hasMl360SelfSubscription: boolean;
    hasMl360BuddySubscription: boolean;
    hasMl360TrainerSubscription: boolean;
    hasTeamResilienceSubscription: boolean;
    hasSelfDeterminationSubscription: boolean;
    hasIndividualMotivationSubscription: boolean;
    // Generic predicate for flexible checks
    hasEnabledModule: (options: {
        identifiers?: string[];
        category?: ModuleCategory;
        subCategory?: ModuleSubCategory;
    }) => boolean;
}

/**
 * useCompanyModuleAccess
 *
 * Derives coarse and fine-grained module access from the company's enabled module identifiers,
 * cross-referenced with the full set of available module definitions.
 *
 * Data sources:
 * - Company.enabledModules (identifiers)
 * - availableModules (category/subCategory for each identifier)
 */
const useCompanyModuleAccess = (companyId: string): CompanyModuleAccessHook => {
    const { data: companyData, loading: companyLoading } = useFetchCompanyDetailsQuery({
        variables: { id: companyId },
        fetchPolicy: 'cache-and-network',
    });

    const enabledIdentifiers: string[] = useMemo(() => companyData?.company?.enabledModules ?? [], [companyData]);

    // Fetch module definitions (small bounded set); filter client-side by identifier
    const { data: availableData, loading: availableLoading } = useFetchAvailableModulesQuery({
        variables: { filter: { identifiers: enabledIdentifiers } },
        fetchPolicy: 'cache-and-network',
    });

    const companyModuleDefs = useMemo(() => availableData?.availableModules?.items ?? [], [availableData]);

    const hasEnabledModule = useMemo(() => {
        return (options: {
            identifiers?: string[];
            category?: ModuleCategory;
            subCategory?: ModuleSubCategory;
        }): boolean => {
            if (options.identifiers && options.identifiers.length > 0) {
                return companyModuleDefs.some(def => options.identifiers!.includes(def.identifier));
            }
            return companyModuleDefs.some(def => {
                const category = def.category;
                const subCategory = 'subCategory' in def ? def.subCategory : null;
                if (options.category && category !== options.category) {
                    return false;
                }
                if (
                    options.subCategory !== undefined &&
                    options.subCategory !== null &&
                    subCategory !== options.subCategory
                ) {
                    return false;
                }
                return true;
            });
        };
    }, [companyModuleDefs]);

    // Coarse checks
    const hasAssessments = useMemo(() => hasEnabledModule({ category: ModuleCategory.Assessment }), [hasEnabledModule]);
    const hasTraining = useMemo(() => hasEnabledModule({ category: ModuleCategory.Training }), [hasEnabledModule]);
    const hasReporting = useMemo(() => hasEnabledModule({ category: ModuleCategory.Reporting }), [hasEnabledModule]);
    const hasPerformance = useMemo(
        () => hasEnabledModule({ category: ModuleCategory.External, subCategory: ModuleSubCategory.Performance }),
        [hasEnabledModule]
    );

    // Fine-grained assessment checks by subCategory
    const hasCognitiveAssessments = useMemo(
        () => hasEnabledModule({ category: ModuleCategory.Assessment, subCategory: ModuleSubCategory.Cognitive }),
        [hasEnabledModule]
    );
    const hasPsychologicalAssessments = useMemo(
        () => hasEnabledModule({ category: ModuleCategory.Assessment, subCategory: ModuleSubCategory.Psychological }),
        [hasEnabledModule]
    );
    const hasPhysiologicalAssessments = useMemo(
        () => hasEnabledModule({ category: ModuleCategory.Assessment, subCategory: ModuleSubCategory.Physical }),
        [hasEnabledModule]
    );

    const hasWeightSubscription = useMemo(
        () =>
            hasEnabledModule({
                identifiers: ['weight'],
            }),
        [hasEnabledModule]
    );

    const hasIpptSubscription = useMemo(
        () =>
            hasEnabledModule({
                identifiers: ['ippt'],
            }),
        [hasEnabledModule]
    );
    const hasSocSubscription = useMemo(
        () =>
            hasEnabledModule({
                identifiers: ['soc'],
            }),
        [hasEnabledModule]
    );
    const has20kmMarchSubscription = useMemo(
        () =>
            hasEnabledModule({
                identifiers: ['20km-road-march'],
            }),
        [hasEnabledModule]
    );
    const hasFinswimSubscription = useMemo(
        () =>
            hasEnabledModule({
                identifiers: ['fin-swim'],
            }),
        [hasEnabledModule]
    );
    const hasBmpSubscription = useMemo(
        () =>
            hasEnabledModule({
                identifiers: ['bmp'],
            }),
        [hasEnabledModule]
    );

    // check cognitive module subscription
    const hasSartSubscription = useMemo(
        () =>
            hasEnabledModule({
                identifiers: ['sart'],
            }),
        [hasEnabledModule]
    );
    const hasRxnSubscription = useMemo(
        () =>
            hasEnabledModule({
                identifiers: ['visual-rxn'],
            }),
        [hasEnabledModule]
    );
    const hasSpatialPlanningSubscription = useMemo(
        () =>
            hasEnabledModule({
                identifiers: ['spatial-planning'],
            }),
        [hasEnabledModule]
    );

    // check psychological module subscription
    const hasMl360SelfSubscription = useMemo(
        () =>
            hasEnabledModule({
                identifiers: ['ml360-self'],
            }),
        [hasEnabledModule]
    );
    const hasMl360BuddySubscription = useMemo(
        () =>
            hasEnabledModule({
                identifiers: ['ml360-buddy'],
            }),
        [hasEnabledModule]
    );
    const hasMl360TrainerSubscription = useMemo(
        () =>
            hasEnabledModule({
                identifiers: ['ml360-trainer'],
            }),
        [hasEnabledModule]
    );
    const hasTeamResilienceSubscription = useMemo(
        () =>
            hasEnabledModule({
                identifiers: ['team-resilience'],
            }),
        [hasEnabledModule]
    );
    const hasSelfDeterminationSubscription = useMemo(
        () =>
            hasEnabledModule({
                identifiers: ['self-determination'],
            }),
        [hasEnabledModule]
    );
    const hasIndividualMotivationSubscription = useMemo(
        () =>
            hasEnabledModule({
                identifiers: ['individual-motivation-questionnaire'],
            }),
        [hasEnabledModule]
    );

    // Reporting checks by identifier (stable and explicit)
    const hasIndividualReport = useMemo(
        () => hasEnabledModule({ identifiers: ['individual-report'] }),
        [hasEnabledModule]
    );
    const hasTeamResilienceReport = useMemo(
        () => hasEnabledModule({ identifiers: ['team-resilience-report'] }),
        [hasEnabledModule]
    );

    return {
        loading: !!companyId && (companyLoading || availableLoading),
        companyId,
        hasAssessments,
        hasTraining,
        hasReporting,
        hasCognitiveAssessments,
        hasPsychologicalAssessments,
        hasPhysiologicalAssessments,
        hasIndividualReport,
        hasTeamResilienceReport,
        hasEnabledModule,
        hasWeightSubscription,
        hasIpptSubscription,
        hasSocSubscription,
        has20kmMarchSubscription,
        hasFinswimSubscription,
        hasBmpSubscription,
        hasPerformance,
        hasSartSubscription,
        hasRxnSubscription,
        hasSpatialPlanningSubscription,
        hasMl360SelfSubscription,
        hasMl360BuddySubscription,
        hasMl360TrainerSubscription,
        hasTeamResilienceSubscription,
        hasSelfDeterminationSubscription,
        hasIndividualMotivationSubscription,
    };
};

export default useCompanyModuleAccess;
